import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import Layout from "../HorizontalMenu/Menu";
import Navbar from "../HorizontalMenu/Navbar";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "index";
import { getVesselsAction, setVoyageID, setOnboardedVessels } from "Store/Generic/ReportingSlice";
import { OnboardingStatus, Roles } from "shared/constants";
import DashboardFleet from "./DashboardFleet";
import { hasRole, userVessels } from "utils/auth/authUtils";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { useQuery } from "react-query";
import { queryKeyes } from "shared/queryKeys";
import VesselCard from "Components/VesselCard";
import Pending from "./PendingFiles/Pending";
import ErrorComponent from "Components/ErrorComponent";
import MessageCard from "./MessageCard";
import { useDocumentTitle } from "Components/useDocument.hooks";
import VesselPositionMap from "./VesselPositionMap";
import env from "environment_system/env_system";
import ClientLogo from '../Media/client_logo.png';
import Defaultlogo from '../Media/Logo-01.jpg';
import AssesmentCards from "./AssementCard";
import VesselCardLoading from "Components/vesselCardLoading";
import SystemUnderMaintenance from "Components/SystemUnderMaintenance";

const Dashboard = () => {
    /** State variables */
    const { Vessels, VesselID } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const [userAssignedVessels, setUserAssignedVessels] = useState<Array<any>>([]);
    const Logo = ClientLogo || Defaultlogo;
    let applicableVessels = useMemo(() => userVessels(), []);

    useDocumentTitle("Dashboard - ecoSAIL");
    /** State varibles end */

    /** Queries */
    /** Vessel card details */
    const { data: CardDetails, isLoading: CardDetailsLoading, isError: CardDetailsError } = useQuery(
        [queryKeyes.vessel.VesselCardDetails.key],
        async () => {
            return await Get(queryKeyes.vessel.VesselCardDetails.url())
        },
        {
            enabled: true,
        }
    );
    /** Vessel's last report */
    const { data: LastVesselReport } = useQuery(
        [queryKeyes.vessel.LastVesselReport.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.LastVesselReport.url(VesselID), VesselID)
        },
        {
            enabled: true,
        }
    );
    /** Organization configuration settings */
    const { data: orgConfig } = useQuery(
        [queryKeyes.organization.OrganizationConfiguration.key],
        async () => await Get(queryKeyes.organization.OrganizationConfiguration.url()),
        { staleTime: Infinity }
    );
    /** Queries end */

    /** Effect 1: Fetch vessels on mount */
    useEffect(() => {
        dispatch(getVesselsAction("vessel_master" as string));
    }, [dispatch]);

    /** Effect 2: Set onboarded vessels when Vessels data is available */
    useEffect(() => {
        if (Array.isArray(Vessels)) {
            const onboardedVessels = Vessels.filter(
                (vessel) => vessel.onboarding_status === OnboardingStatus.ONBOARDED
            );
            dispatch(setOnboardedVessels(onboardedVessels));
        }
    }, [Vessels, dispatch]);

    useEffect(() => {
        if (LastVesselReport !== undefined) {
            dispatch(setVoyageID(LastVesselReport?.voyage_information))
        }
    }, [LastVesselReport, dispatch])

    useEffect(() => {
        if (CardDetails && applicableVessels?.length > 0) {
            const filteredVessels = applicableVessels
                .map((vesselId: number) =>
                    CardDetails.find((item: any) => item.vessel_id === vesselId)
                )
                .filter(Boolean); // removes undefined entries
            setUserAssignedVessels(filteredVessels);
        }
    }, [CardDetails, applicableVessels]);
    /** useEffect end */

    return (
        <React.Fragment>
            {(!orgConfig?.is_system_under_maintenance || hasRole(Roles.ES_ADMIN)) &&
                <Layout>
                    <Navbar />
                </Layout>
            }
            {orgConfig?.is_system_under_maintenance && !hasRole(Roles.ES_ADMIN) &&
                <SystemUnderMaintenance orgConfig={orgConfig} />
            }
            <div className="page-content">
                <Container fluid>
                    {(!orgConfig?.is_system_under_maintenance || hasRole(Roles.ES_ADMIN)) &&
                        <Row className="mb-3">
                            <div className="d-flex justify-content-center client-logo-container">
                                <img
                                    src={Logo}
                                    alt={`${env?.company_title}_logo`}
                                    className="client-logo me-3"
                                />
                                <h2 className="mb-0 center-vertically">{env?.company_title}</h2>
                            </div>
                        </Row>
                    }
                    {CardDetailsError && <ErrorComponent message="Error loading required data!" />}
                    {!CardDetailsError && (!orgConfig?.is_system_under_maintenance || hasRole(Roles.ES_ADMIN)) &&
                        (applicableVessels?.length === 0 ? <MessageCard /> : (
                            <React.Fragment>
                                {(hasRole(Roles.ES_ADMIN) || hasRole(Roles.MANAGER) || hasRole(Roles.ADMIN)) && Vessels &&
                                    <AssesmentCards />
                                }
                                {(hasRole(Roles.ES_ADMIN) ||
                                    hasRole(Roles.MANAGER) ||
                                    hasRole(Roles.ADMIN)) &&
                                    <DashboardFleet />
                                }
                                {(hasRole(Roles.CHIEFENGINEER) || hasRole(Roles.MASTER)) &&
                                    <Row className="mt-3">
                                        {(hasRole(Roles.CHIEFENGINEER) || hasRole(Roles.MASTER)) && (
                                            <Row className="mt-3">
                                                {CardDetailsLoading ? (
                                                    <Row>
                                                        <Col lg={3} className="pe-0">
                                                            <VesselCardLoading />
                                                        </Col>
                                                    </Row>
                                                ) : (
                                                    <VesselCard Vessels={userAssignedVessels} />
                                                )}
                                            </Row>
                                        )}

                                    </Row>
                                }
                                <Row className="px-2 mt-3">
                                    {!(!env?.offlineCopy && Vessels?.filter((vessel: any) => vessel?.onboarding_status === OnboardingStatus.ONBOARDED)?.every((vessel: any) => vessel?.is_vessel_offline === true)) &&
                                        <Col
                                            lg={8}
                                            className="px-0"
                                        >
                                            <Pending />
                                        </Col>
                                    }
                                    <Col lg={4} className="pe-0">
                                        <VesselPositionMap />
                                    </Col>
                                </Row>
                            </React.Fragment>
                        ))}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Dashboard;